import {
  InstrumentPanel,
  QuickView,
  QuickViewControls,
} from 'lib-analytics-types';
import { ampli } from 'lib-shared-tracking';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { useCallback, useMemo, useState } from 'react';

import { analyticsApi } from 'apis/analytics';
import { appActions } from 'app/app.actions';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useNavigatePrompt } from 'utils/url/useNavigatePrompt';
import { useCurrentUser } from 'utils/user';

import { mergePanelAndQuickViewControls } from '../mergePanelAndQuickViewControls';

import { UseQuickViewManagerResult } from './useQuickViewManager.types';

export const useQuickViewManager = (
  quickView: QuickView,
): UseQuickViewManagerResult => {
  const dispatch = useAppDispatch();
  const notify = useNotifications();
  const { user } = useCurrentUser();

  const [controls, setControls] = useState(quickView.controls ?? null);
  const [panels, setPanels] = useState(quickView.panels);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const isOwner = quickView.user.id === user.id;

  useNavigatePrompt(isOwner && hasUnsavedChanges);

  const [updateQuickView] = analyticsApi.useUpdateQuickViewMutation();
  const [updateQuickViewPanels] =
    analyticsApi.useUpdateQuickViewPanelsMutation();

  const handlePinPanel: UseQuickViewManagerResult['handlePinPanel'] =
    useCallback(
      (panel) => {
        setPanels((prevPanels) => [...prevPanels, panel]);
        setHasUnsavedChanges(true);

        ampli.analyticsQuickViewPanelPinned({
          panel: panel.title,
          shared: quickView.shared,
          hasDescription: !!panel.description,
        });
      },
      [quickView.shared],
    );

  const handleUnpinPanel: UseQuickViewManagerResult['handleUnpinPanel'] =
    useCallback(
      (panel) => {
        setPanels((prevPanels) =>
          prevPanels.filter(({ id }) => id !== panel.id),
        );
        setHasUnsavedChanges(true);

        ampli.analyticsQuickViewPanelUnpinned({
          panel: panel.title,
          shared: quickView.shared,
        });
      },
      [quickView.shared],
    );

  const handleChangeLayout: UseQuickViewManagerResult['handleChangeLayout'] =
    useCallback(
      (layout, event) => {
        const newPanels = layout.reduce((acc, { i: panelId, h, w, x, y }) => {
          const panel = panels.find(({ id }) => id === panelId);
          return panel ? [...acc, { ...panel, layout: { h, w, x, y } }] : acc;
        }, [] as readonly InstrumentPanel[]);

        setPanels(newPanels);
        setHasUnsavedChanges(true);

        ampli.analyticsQuickViewPanelsLayoutChanged({
          event,
          shared: quickView.shared,
        });
      },
      [panels, quickView.shared],
    );

  const handleChangePanelControls: UseQuickViewManagerResult['handleChangePanelControls'] =
    useCallback(
      (panel, applyToAll) => {
        setPanels((prevPanels) =>
          prevPanels.map((prevPanel) => {
            if (!panel.controls) return prevPanel;

            if (prevPanel.id === panel.id) {
              if (panel.description !== prevPanel.description) {
                ampli.analyticsQuickViewPanelDescriptionUpdated({
                  panel: panel.title,
                  shared: quickView.shared,
                });
              }

              return {
                ...prevPanel,
                controls: panel.controls,
                description: panel.description,
              };
            }

            if (applyToAll) {
              return {
                ...prevPanel,
                controls: {
                  ...panel.controls,
                  settings: {
                    ...prevPanel.controls!.settings,
                    // When applying controls to all panels, we remove the settings that are not
                    // shared, as they are not applicable to all panels and may cause issues.
                    ...omit(panel.controls.settings, [
                      'granularity',
                      'visualization',
                    ]),
                  },
                },
              };
            }

            return prevPanel;
          }),
        );
        setHasUnsavedChanges(true);

        ampli.analyticsQuickViewPanelControlsUpdated({
          panel: panel.title,
          shared: quickView.shared,
          applyToAll,
        });
      },
      [quickView.shared],
    );

  const handleChangeQuickViewControls: UseQuickViewManagerResult['handleChangeQuickViewControls'] =
    useCallback(
      (updatedControls) => {
        const quickViewControls = updatedControls
          ? ({
              ...controls,
              ...updatedControls,
              settings: {
                ...controls?.settings,
                ...updatedControls.settings,
              },
            } as QuickViewControls)
          : null;

        setControls(quickViewControls);

        // All panel controls are overwritten by the global quick view controls.
        setPanels((prevPanels) =>
          prevPanels.map((panel) => ({
            ...panel,
            controls: mergePanelAndQuickViewControls(panel, quickViewControls),
          })),
        );

        setHasUnsavedChanges(true);
      },
      [controls],
    );

  const handleUndoChanges: UseQuickViewManagerResult['handleUndoChanges'] =
    useCallback(() => {
      setControls(quickView.controls ?? null);
      setPanels(quickView.panels);
      setHasUnsavedChanges(false);

      ampli.analyticsQuickViewPanelsReset({ shared: quickView.shared });
    }, [quickView.controls, quickView.panels, quickView.shared]);

  const handleSaveChanges: UseQuickViewManagerResult['handleSaveChanges'] =
    useCallback(async () => {
      if (!quickView) return;

      const { isConfirmed } = await dispatch(
        appActions.showModal({
          modal: {
            type: 'app:confirm',
            arg: {
              title: `Save dashboard`,
              description: (
                <>
                  Any changes made to <b>{quickView.name}</b> will be saved,
                  including moving, resizing and removing panels, as well as
                  updated filters. Do you want to continue?
                </>
              ),
              confirm: {
                label: 'Yes, save changes',
                variant: 'primary',
              },
            },
          },
        }),
      );

      if (isConfirmed) {
        try {
          // If the global quick view controls changed, update the quick view.
          if (!isEqual(quickView.controls, controls)) {
            await updateQuickView({
              id: quickView.id,
              quickView: {
                controls,
                name: quickView.name,
                shared: quickView.shared,
              },
            });
          }

          const {
            data: { quick_view: updatedQuickView },
          } = await updateQuickViewPanels({
            quickViewId: quickView.id,
            panels,
          }).unwrap();

          notify(
            {
              title: 'Success',
              text: `Changes to "${quickView.name}" have been saved.`,
              icon: 'i-done',
              time: 5000,
            },
            NotificationType.Success,
          );

          ampli.analyticsQuickViewPanelsUpdated({
            shared: updatedQuickView.shared,
            totalPanels: updatedQuickView.panels.length,
          });

          setHasUnsavedChanges(false);

          return Promise.resolve({ quickView: updatedQuickView });
        } catch (error) {
          notify(
            {
              title: 'Error while saving the dashboard.',
            },
            NotificationType.Error,
          );
        }
      }
    }, [
      controls,
      dispatch,
      notify,
      panels,
      quickView,
      updateQuickView,
      updateQuickViewPanels,
    ]);

  return useMemo(
    () => ({
      controls,
      panels,
      hasUnsavedChanges,
      handlePinPanel,
      handleUnpinPanel,
      handleUndoChanges,
      handleChangeLayout,
      handleChangePanelControls,
      handleChangeQuickViewControls,
      handleSaveChanges,
    }),
    [
      controls,
      panels,
      hasUnsavedChanges,
      handlePinPanel,
      handleUnpinPanel,
      handleUndoChanges,
      handleChangeLayout,
      handleChangePanelControls,
      handleChangeQuickViewControls,
      handleSaveChanges,
    ],
  );
};
